import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RouterProvider, ApiProvider, NotificationProvider, ModuleAccessProvider, withTenantSlug } from "@viilutalous/common";
import { useTenant } from './TenantContext';

/**
 * ViiluCommonPackageProviders provides the required providers to be able to use common ui packages
 * Provides e.g. routing and api configuration in such a way that "Viilu Isännöinti" can also provide their own implementations
 */
export function ViiluCommonPackageProviders({ children }) {
  const reactRouterNavigate = useNavigate();
  const params = useParams(); // Get URL params from react-router
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const { currentTenant } = useTenant();

  // Router functions - modules call navigate with relative paths like '/dashboard'.
  // We turn them into /app/{tenantSlug}/dashboard so shared deep links preserve the
  // tenant for the recipient as they navigate further into the app.
  const navigate = useCallback((path, options = {}) => {
    const slug = currentTenant?.slug;
    const fullPath = slug ? withTenantSlug(path, slug) : `/app${path}`;
    reactRouterNavigate(fullPath, options);
  }, [reactRouterNavigate, currentTenant?.slug]);

  const getHref = useCallback((path) => (
    currentTenant?.slug ? withTenantSlug(path, currentTenant.slug) : undefined
  ), [currentTenant?.slug]);

  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  // API configuration
  const apiUrl = import.meta.env.VITE_API_URL || "/api";

  // Prefer a fresh Firebase ID token (required by isännöinti finance).
  // Fall back to localStorage when auth.currentUser is not ready yet.
  const getAuthBearerToken = useCallback(async () => {
    try {
      const { auth } = await import("../modules/auth");
      if (auth?.currentUser) {
        const token = await auth.currentUser.getIdToken();
        localStorage.setItem("access_token", token);
        return token;
      }
    } catch (e) {
      console.warn("Could not refresh Firebase ID token:", e);
    }
    return localStorage.getItem("access_token");
  }, []);

  return (
    <RouterProvider
      navigate={navigate}
      getHref={getHref}
      goBack={goBack}
      params={params}
      location={{
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: location.state,
      }}
      searchParams={searchParams}
    >
      <ApiProvider apiUrl={apiUrl} getAuthBearerToken={getAuthBearerToken}>
        <NotificationProvider>
          <ModuleAccessProvider>
            {children}
          </ModuleAccessProvider>
        </NotificationProvider>
      </ApiProvider>
    </RouterProvider>
  );
}
